import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import rttShaderVertex from "./shaders/rttshader/vertex.glsl";
import rttFragmentVertex from "./shaders/rttshader/fragment.glsl";
import fireFliesVertexShader from "./shaders/fireflies/vertex.glsl";
import fireFliesFragmentShader from "./shaders/fireflies/fragment.glsl";
import "./SCSS/index.scss";

class MainScenerCtrl {
  constructor() {
    this.canvas = undefined;
    this.sizes = undefined;
    this.scene = undefined;
    this.rttScene = undefined;
    this.renderer = undefined;
    this.camera = undefined;
    this.controls = undefined;
    // this.plane = undefined;
    this.textureLoader = new THREE.TextureLoader();
    this.dracoLoader = new DRACOLoader();
    this.dracoLoader.setDecoderPath("/draco/");
    this.gltfLoader = new GLTFLoader();
    this.gltfLoader.setDRACOLoader(this.dracoLoader);
    this.material = undefined;
    this.fireFliesMaterial = undefined;
    this.clock = undefined;
    this.rafID = undefined;
  }

  setScene() {
    this.canvas = document.querySelector("canvas.webgl");
    this.sizes = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    window.addEventListener("resize", () => {
      this.sizes.width = window.innerWidth;
      this.sizes.height = window.innerHeight;

      this.camera.aspect = this.sizes.width / this.sizes.height;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(this.sizes.width, this.sizes.height);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      this.material.uniforms.windowWidth.value = window.innerWidth;
      this.material.uniforms.windowHeight.value = window.innerHeight;
    });

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
    });
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputEncoding = THREE.sRGBEncoding;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color("#201919");
    this.camera = new THREE.PerspectiveCamera(
      75,
      this.sizes.width / this.sizes.height,
      0.1,
      100
    );

    this.camera.position.set(4, 2, 4);
    this.scene.add(this.camera);

    this.controls = new OrbitControls(this.camera, this.canvas);

    this.material = new THREE.ShaderMaterial({
      fragmentShader: rttFragmentVertex,
      vertexShader: rttShaderVertex,
      uniforms: {
        windowWidth: {
          value: window.innerWidth,
        },
        windowHeight: {
          value: window.innerHeight,
        },
        uTime: {
          value: 0,
        },
        uTexture: {
          value: undefined,
        },
      },
    });

    // this.plane = new THREE.Mesh(
    //   new THREE.PlaneBufferGeometry(1, 1, 10, 10),
    //   new THREE.MeshBasicMaterial({ color: "red" })
    // );

    // this.scene.add(this.plane);

    this.gltfLoader.load(
      "models/portal/portal.glb",
      (gltf) => {
        const bakedTexture = this.textureLoader.load(
          "textures/portal_baked.jpg"
        );
        bakedTexture.flipY = false;
        bakedTexture.encoding = THREE.sRGBEncoding;

        const bakedMaterial = new THREE.MeshBasicMaterial({
          map: bakedTexture,
        });
        const poleLightMaterial = new THREE.MeshBasicMaterial({
          color: 0xffffef,
        });

        const bakedMesh = gltf.scene.children.find((child) => {
          return child.name === "baked";
        });

        const poleLightAMesh = gltf.scene.children.find((child) => {
          return child.name === "poleLightA";
        });

        const poleLightBMesh = gltf.scene.children.find((child) => {
          return child.name === "poleLightB";
        });

        const poleLightCMesh = gltf.scene.children.find((child) => {
          return child.name === "poleLightC";
        });

        const poleLightDMesh = gltf.scene.children.find((child) => {
          return child.name === "poleLightD";
        });

        const portalLight = gltf.scene.children.find((child) => {
          return child.name === "portalLight";
        });

        portalLight.rotation.set(Math.PI * 0.5, Math.PI, 0);

        bakedMesh.material = bakedMaterial;
        poleLightAMesh.material = poleLightMaterial;
        poleLightBMesh.material = poleLightMaterial;
        poleLightCMesh.material = poleLightMaterial;
        poleLightDMesh.material = poleLightMaterial;
        portalLight.material = this.material;
        // this.plane.material = this.material;

        this.controls.target.copy(portalLight.position);

        this.scene.add(gltf.scene);
        // bakedMesh.visible = false;
      },
      (progress) => {},
      (error) => {
        console.log(error);
      }
    );

    const fireFliesGeometry = new THREE.BufferGeometry();
    const fireFliesCount = 30;
    const positionArray = new Float32Array(fireFliesCount * 3);
    const scaleArray = new Float32Array(fireFliesCount);

    for (let i = 0; i < fireFliesCount; i++) {
      positionArray[i * 3 + 0] = (Math.random() - 0.5) * 4;
      positionArray[i * 3 + 1] = Math.random() * 1.5;
      positionArray[i * 3 + 2] = (Math.random() - 0.5) * 4;

      scaleArray[i] = Math.random();
    }

    fireFliesGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(positionArray, 3)
    );

    fireFliesGeometry.setAttribute(
      "aScale",
      new THREE.BufferAttribute(scaleArray, 1)
    );

    this.fireFliesMaterial = new THREE.ShaderMaterial({
      vertexShader: fireFliesVertexShader,
      fragmentShader: fireFliesFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      uniforms: {
        uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
        uSize: { value: 250 },
        uTime: { value: 0 },
        uColor: { value: new THREE.Color("#ffffff") },
      },
    });

    const fireFlies = new THREE.Points(
      fireFliesGeometry,
      this.fireFliesMaterial
    );
    this.scene.add(fireFlies);

    this.clock = new THREE.Clock();
    this.render();
  }

  setRTTScene(rttScene) {
    this.rttScene = rttScene;
    this.applyRTTtoUniform();
  }

  onLoadRTTSceneRoom() {
    this.scene.add(this.rttScene.roomMesh);
  }

  applyRTTtoUniform() {
    if (!this.rttScene) {
      return;
    }
    this.rttScene.renderTarget.texture.wrapS = THREE.RepeatWrapping;
    this.rttScene.renderTarget.texture.wrapT = THREE.RepeatWrapping;

    console.log(this.rttScene.renderTarget.texture);
    this.material.uniforms.uTexture.value = this.rttScene.renderTarget.texture;
  }

  play() {
    if (this.rafID) {
      return;
    }
    this.tick();
  }

  render() {
    if (this.rttScene) {
      this.renderer.setRenderTarget(this.rttScene.renderTarget);
      this.renderer.render(this.rttScene.scene, this.rttScene.camera);
      this.renderer.setRenderTarget(null);
    }
    this.renderer.render(this.scene, this.camera);
  }

  tick() {
    const elapsedTime = this.clock.getElapsedTime();

    //Update uTime
    this.material.uniforms.uTime.value = elapsedTime;

    //Update fireflies
    this.fireFliesMaterial.uniforms.uTime.value = elapsedTime;

    this.controls.update();

    if (this.rttScene) {
      this.rttScene.updateCamPosition(this.camera);
    }
    this.render();
    this.rafID = requestAnimationFrame(this.tick.bind(this));
  }
}

export default MainScenerCtrl;
